import React, { useEffect, useState } from "react";
import styled from "styled-components";

import { ReactComponent as SmallChat } from "../../Assets/icons/small-chats-icons.svg";
import { ReactComponent as SmallPhone } from "../../Assets/icons/small-phone-icons.svg";
import { ReactComponent as ChatNameIcon } from "../../Assets/icons/chatNameInputIcon.svg";
import { ReactComponent as Globe } from "../../Assets/iconsv2/sidebar/GlobeIcon.svg";
import { useAuthService } from "../../Context/AuthContext";
import LanguageBtn from "../Auth Components/LanguageBtn";
import { useTranslate, useTolgee } from "@tolgee/react";
import PrimaryButton from "../GenericComponents/PrimaryButton";
import { INTERFACE_TRANSLATION_LANGUAGES } from "../../Constants/constantValues";
import LocalStorage from "../../Helpers/LocalStorage";

const OnboardConferenceAnonymousUser = ({ onComplete, onCancel }) => {
  const { t } = useTranslate();
  const tolgee = useTolgee();
  const {
    translationsLanguages,
    callTranslationsLanguages,
    getCallTranslationsLanguages,
    getTranslationsLanguages,
    user,
  } = useAuthService();

  const [name, setName] = useState(user?.name || "");
  const [language, setLanguage] = useState(user?.language || "");
  const [interfaceLanguage, setInterfaceLanguage] = useState(
    tolgee.getLanguage()
  );
  const [callLanguage, setCallLanguage] = useState(user?.call_language || "");

  useEffect(() => {
    getCallTranslationsLanguages(() => {}, () => {});
    getTranslationsLanguages(() => {}, () => {});
  }, [getCallTranslationsLanguages, getTranslationsLanguages]);

  useEffect(() => {
    if (!user) return;
    setName(user.name || "");
    setLanguage(user.language || "");
    setCallLanguage(user.call_language || "");
  }, [user]);

  const isFormValid =
    name.trim().length >= 3 && language && callLanguage && interfaceLanguage;

  return (
    <Container>
      <Content>
        <Card>
          <GroupTitle>
            <h1>{t("onboardingPage.app_preference")}</h1>
            <p>{t("conferences.joining_conference")}</p>
          </GroupTitle>
          <Group>
            <GroupDescription>
              <GroupDescriptionText>{t("general.interface_language")}</GroupDescriptionText>
            </GroupDescription>
            <LanguageBtn
              value={interfaceLanguage}
              setValue={(value) => {
                setInterfaceLanguage(value);
                tolgee.changeLanguage(value);
                LocalStorage.setInterfaceLanguage(value);
              }}
              valuesList={INTERFACE_TRANSLATION_LANGUAGES}
              placeholder={t("general.interface_language_placeholder")}
              placeholderIcon={<Globe />}
              callLanguage
            />
          </Group>
          <Group>
            <GroupDescription>
              <GroupDescriptionText>{t("register.chat_language")}</GroupDescriptionText>
            </GroupDescription>
            <LanguageBtn
              value={language}
              setValue={setLanguage}
              valuesList={translationsLanguages}
              placeholder={t("register.chat_language_placeholder")}
              placeholderIcon={<SmallChat />}
            />
          </Group>
          <Group>
            <GroupDescription>
              <GroupDescriptionText>{t("onboardingPage.calls_language")}</GroupDescriptionText>
            </GroupDescription>
            <LanguageBtn
              value={callLanguage}
              setValue={setCallLanguage}
              valuesList={callTranslationsLanguages}
              placeholder={t("onboardingPage.select_calls_placeholder")}
              placeholderIcon={<SmallPhone />}
              callLanguage
            />
          </Group>
          <Group>
            <GroupDescription>
              <GroupDescriptionText>{t("general.your_name")}</GroupDescriptionText>
            </GroupDescription>
            <CustomInputWrapper>
              <ChatNameIcon />
              <input
                placeholder={t("register.name_placeholder")}
                onChange={(e) => setName(e.currentTarget.value)}
                value={name}
                name="name"
                maxLength={100}
              />
            </CustomInputWrapper>
          </Group>

          <Group style={{ alignItems: "center", justifyContent: "center" }}>
            <PrimaryButton
              style={{ padding: "16px", width: "100%" }}
              disabled={!isFormValid}
              onClick={() =>
                onComplete({
                  name: name.trim(),
                  language,
                  callLanguage,
                  interfaceLanguage,
                })
              }
            >
              {t("conferences.join_conference")}
            </PrimaryButton>
            {onCancel && (
              <CancelBtn type="button" onClick={onCancel}>
                {t("general.cancel")}
              </CancelBtn>
            )}
          </Group>
        </Card>
      </Content>
    </Container>
  );
};

export default OnboardConferenceAnonymousUser;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  max-height: 100vh;
  height: 100%;
  width: 100%;
  align-items: center;
  position: relative;
  overflow: hidden;
  background: ${({ theme }) => theme.menu.background};
`;
const Card = styled.div`
  display: flex;
  flex-direction: column;
  max-width: 500px;
  width: 100%;
  gap: 40px;
  margin: auto;
`;
const Group = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  h1 {
    color: ${({ theme }) => theme.menu.primaryTextColor};
    font-size: 32px;
    font-weight: 600;
    line-height: 39px;
    letter-spacing: 0px;
    text-align: left;
  }
`;
const CustomInputWrapper = styled.div`
  display: flex;
  width: 100%;
  background: ${({ theme }) => theme.menu.groupContentBackground};
  align-items: center;
  padding: 16px;
  border-radius: 4px;
  gap: 10px;

  input {
    width: 100%;
    color: ${({ theme }) => theme.menu.primaryTextColor};
    background: ${({ theme }) => theme.menu.groupContentBackground};
    border: none;
    font-size: 16px;
    font-weight: 400;
    line-height: 22px;
    text-align: left;
    &:focus {
      outline: none;
    }
    &::placeholder {
      color: ${({ theme }) => theme.menu.primaryTextColor};
      opacity: 0.6;
    }
  }
`;
const GroupTitle = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  align-items: center;
  margin-bottom: 20px;
  h1 {
    color: ${({ theme }) => theme.menu.primaryTextColor};
    font-size: 32px;
    font-weight: 600;
    line-height: 39px;
    letter-spacing: 0px;
    text-align: left;
  }
  p {
    color: ${({ theme }) => theme.menu.secondaryTextColor};
    font-size: 16px;
    font-weight: 400;
    line-height: 22px;
    margin: 0;
  }
`;
const GroupDescription = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const GroupDescriptionText = styled.p`
  color: ${({ theme }) => theme.menu.primaryTextColor};
  font-size: 16px;
  font-weight: 500;
  line-height: 19px;
`;
const Content = styled.div`
  display: flex;
  width: 100%;
  height: 100%;
`;

const CancelBtn = styled.button`
  font-size: 14px;
  font-weight: 500;
  line-height: 24px;
  color: ${({ theme }) => theme.menu.primaryTextColor};
  margin-top: 10px;
  text-decoration: underline;
  border: none;
  background: transparent;
  cursor: pointer;
`;
